import {useState} from "react";
import {toast} from "sonner";
import {Card} from "@/components/ui/card.tsx";
import {ScrollArea} from "@/components/ui/scroll-area.tsx";
import {Badge} from "@/components/ui/badge.tsx";
import {Spinner} from "@/components/ui/spinner.tsx";
import {Button} from "@/components/ui/button.tsx";
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from "@/components/ui/tooltip.tsx";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog.tsx";
import {cn} from "@/lib/utils.ts";
import {useDeleteSClassTestMutation, useGetSClassTestsQuery} from "@/store/sclassTest.ts";
import {SClassTest} from "@/types/SClassTest.ts";
import {ClipboardDocumentListIcon, TrashIcon} from "@heroicons/react/24/outline";
import {formatApiError} from "@/pages/sclass-test/apiError.ts";

type SClassTestListPanelProps = {
    selectedId: number;
    onSelect: (id: number) => void;
    /** נקרא לאחר מחיקה מוצלחת של המקבץ שפתוח כרגע לעריכה, עם מזהה המקבץ הבא לבחירה (אם קיים) */
    onDeletedSelected?: (nextId: number | null) => void;
};

export function SClassTestListPanel({selectedId, onSelect, onDeletedSelected}: SClassTestListPanelProps) {
    const {data: tests, isLoading} = useGetSClassTestsQuery();
    const [deleteSClassTest, {isLoading: isDeleting}] = useDeleteSClassTestMutation();
    const [toDelete, setToDelete] = useState<SClassTest | null>(null);

    async function handleConfirmDelete() {
        if (!toDelete) return;
        try {
            await deleteSClassTest({id: toDelete.id}).unwrap();
            toast.success('מקבץ השאלות נמחק בהצלחה');
            if (toDelete.id === selectedId) {
                const remaining = (tests || []).filter((t) => t.id !== toDelete.id);
                onDeletedSelected?.(remaining[0]?.id ?? null);
            }
        } catch (e: any) {
            toast.error('שגיאה במחיקת מקבץ השאלות', {description: formatApiError(e)});
        } finally {
            setToDelete(null);
        }
    }

    return (
        <Card
            dir="rtl"
            className="p-4 flex flex-col gap-3 h-[calc(100vh-14rem)] lg:sticky lg:top-6 lg:h-[calc(100vh-7.5rem)]"
        >
            <h2 className="text-sm font-medium text-neutral-800 text-right shrink-0">מקבצי שאלות</h2>

            {isLoading && (
                <div className="flex justify-center py-8 shrink-0">
                    <Spinner size="sm"/>
                </div>
            )}

            {!isLoading && tests?.length === 0 && (
                <div className="text-xs text-neutral-400 text-center py-6 shrink-0">
                    לא נמצאו מקבצי שאלות
                </div>
            )}

            {!isLoading && !!tests?.length && (
                <TooltipProvider delayDuration={400}>
                    <ScrollArea dir="rtl" className="flex-1 min-h-0 -mx-1 px-1">
                        <div className="flex flex-col gap-1.5 pb-1" dir="rtl">
                            {tests.map((t) => {
                                const isSelected = t.id === selectedId;
                                const title = t.description || `מקבץ #${t.id}`;
                                return (
                                    <div
                                        key={t.id}
                                        dir="rtl"
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => onSelect(t.id)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                onSelect(t.id);
                                            }
                                        }}
                                        className={cn(
                                            "w-full rounded-lg border px-3 py-2 transition-colors cursor-pointer flex flex-col gap-1.5",
                                            isSelected
                                                ? "border-light-blue-300 border-r-4 border-r-light-blue-600 bg-light-blue-50"
                                                : "border-neutral-200 hover:bg-neutral-50"
                                        )}
                                    >
                                        {/* שורת כותרת: שם המקבץ (ימין) + אייקון מחיקה (שמאל) */}
                                        <div className="flex items-center justify-between gap-2">
                                            <div className="flex items-center gap-2 min-w-0 flex-1">
                                                <ClipboardDocumentListIcon
                                                    className={cn("size-4 shrink-0", isSelected ? "text-light-blue-700" : "text-neutral-400")}
                                                />
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <span className={cn(
                                                            "font-medium text-sm truncate text-right min-w-0",
                                                            isSelected ? "text-light-blue-900" : "text-neutral-800"
                                                        )}>
                                                            {title}
                                                        </span>
                                                    </TooltipTrigger>
                                                    <TooltipContent side="top">{title}</TooltipContent>
                                                </Tooltip>
                                            </div>

                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon-xs"
                                                className="shrink-0 text-neutral-400 hover:bg-red-50 hover:text-red-600"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setToDelete(t);
                                                }}
                                            >
                                                <TrashIcon className="size-4"/>
                                            </Button>
                                        </div>

                                        {/* שורת מידע: שלוחה (ימין) + מספר שאלות (שמאל) */}
                                        <div className={cn(
                                            "flex items-center justify-between gap-2 text-xs",
                                            isSelected ? "text-light-blue-700" : "text-neutral-400"
                                        )}>
                                            <span className="truncate text-right min-w-0">
                                                {t.sclass_name ? `שלוחה: ${t.sclass_name}` : 'ללא שלוחה'}
                                            </span>
                                            <Badge variant="outline"
                                                   className="shrink-0 border-neutral-300 text-neutral-600">
                                                {t.questions_count ?? 0} שאלות
                                            </Badge>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </ScrollArea>
                </TooltipProvider>
            )}

            <AlertDialog open={!!toDelete} onOpenChange={(open) => !open && setToDelete(null)}>
                <AlertDialogContent dir="rtl">
                    <AlertDialogHeader>
                        <AlertDialogTitle>מחיקת מקבץ שאלות</AlertDialogTitle>
                        <AlertDialogDescription>
                            האם אתה בטוח שברצונך למחוק את מקבץ השאלות "{toDelete?.description || `מקבץ #${toDelete?.id}`}"?
                            פעולה זו אינה הפיכה{toDelete?.questions_count ? `, וה-${toDelete.questions_count} שאלות שבתוכו יישארו ללא שיוך למקבץ` : ''}.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>ביטול</AlertDialogCancel>
                        <AlertDialogAction
                            disabled={isDeleting}
                            onClick={handleConfirmDelete}
                            className="bg-red-600 hover:bg-red-700"
                        >
                            מחיקה
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Card>
    );
}

